/**
 * 员工管理 API（staff-rbac-wechat-login:SC-01~SC-12）。
 *
 * 对齐后端 identity 模块 REST 契约：
 * - GET    /staffs                          -> Paginated<StaffListItem>
 * - POST   /staffs                          -> StaffDetail
 * - GET    /staffs/:id                      -> StaffDetail
 * - PATCH  /staffs/:id                      -> StaffDetail
 * - POST   /staffs/:id/suspend              -> StaffDetail
 * - POST   /staffs/:id/activate             -> StaffDetail
 * - PUT    /staffs/:id/grants               -> void
 * - PUT    /staffs/:id/availability          -> void   （5-3 staff-availability）
 * - PUT    /staffs/:id/availability-exceptions -> void （5-3 staff-availability）
 * - POST   /staffs/:id/wechat-bind-qrcode   -> { token }
 * - DELETE /staffs/:id/wechat-bind          -> void
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from "./api.config";
import { http } from "./transport";
import type { Paginated } from "./campuses.api";
import type { WechatQrcodeMode } from "./auth.api";

/** 员工岗位枚举（对齐后端 StaffPosition）。 */
export type StaffPosition =
  | "HQ_ADMIN"
  | "CAMPUS_ADMIN"
  | "ACADEMIC_AFFAIRS"
  | "ENROLLMENT_CONSULTANT"
  | "TEACHER"
  | "TEACHING_ASSISTANT";

/** 岗位中文标签映射。 */
export const staffPositionLabel: Record<StaffPosition, string> = {
  HQ_ADMIN: "总部管理员",
  CAMPUS_ADMIN: "校区管理员",
  ACADEMIC_AFFAIRS: "教务",
  ENROLLMENT_CONSULTANT: "招生顾问",
  TEACHER: "教师",
  TEACHING_ASSISTANT: "助教",
};

/** 新建员工可选岗位：非全机构账号隐藏 HQ_ADMIN（O-29；服务端 403 为准，前端仅防误选）。 */
export function positionOptionsForCreate(hasAllOrgScope: boolean | undefined) {
  const all = (Object.keys(staffPositionLabel) as StaffPosition[]).map((value) => ({
    value,
    label: staffPositionLabel[value],
  }));
  return hasAllOrgScope ? all : all.filter((option) => option.value !== "HQ_ADMIN");
}

/** 员工状态。 */
export type StaffStatus = "ACTIVE" | "SUSPENDED";

/** 员工状态中文标签映射。 */
export const staffStatusLabel: Record<StaffStatus, string> = {
  ACTIVE: "在职",
  SUSPENDED: "已停用",
};

/** 微信绑定状态（用于列表筛选）。 */
export type WechatBindStatus = "BOUND" | "UNBOUND";

/** 星期中文标签映射（dayOfWeek: 1=周一 ... 7=周日）。 */
export const weekdayLabel: Record<number, string> = {
  1: "周一",
  2: "周二",
  3: "周三",
  4: "周四",
  5: "周五",
  6: "周六",
  7: "周日",
};

/** 每周可排课时段。 */
export interface WeeklyAvailabilitySlot {
  /** 时段 ID（后端返回；编辑对话框本地新行无此字段，保存为全量替换）
   *  —— 5-3 起后端响应与详情携带 */
  id?: string;
  /** 星期几（1-7） */
  dayOfWeek: number;
  /** 开始时间 HH:mm */
  startTime: string;
  /** 结束时间 HH:mm */
  endTime: string;
}

/** 临时不可排课日期。 */
export interface AvailabilityException {
  /** 例外 ID（后端返回；本地新行无此字段，保存为全量替换）—— 5-3 起 */
  id?: string;
  /** 日期 YYYY-MM-DD */
  date: string;
  /** 是否全天 */
  allDay: boolean;
  /** 开始时间（非全天时 HH:mm） */
  startTime?: string | null;
  /** 结束时间（非全天时 HH:mm） */
  endTime?: string | null;
  /** 原因（如：事假） */
  reason?: string;
}

/** 员工校区授权项（列表项形态）。 */
export interface StaffCampusGrant {
  /** 校区 ID */
  campusId: string;
  /** 角色 ID 列表 */
  roleIds: string[];
  /** 生效时间（ISO） */
  validFrom?: string;
  /** 失效时间（ISO，空=长期有效） */
  validTo?: string | null;
}

/** 员工列表项（对齐后端 StaffListItemDto，后端已脱敏 mobile）。 */
export interface StaffListItem {
  /** 员工 ID（内部主键） */
  id: string;
  /** 员工编号（staff-{拼音}） */
  staffNo?: string | null;
  /** 姓名 */
  name: string;
  /** 手机号（后端返回脱敏值，如 138****0000） */
  mobile?: string | null;
  /** 岗位列表 */
  positions: StaffPosition[];
  /** 授权校区名称列表（后端派生） */
  campusNames?: string[];
  /** 微信绑定状态 */
  qrBindStatus: WechatBindStatus;
  /** 员工状态 */
  status: StaffStatus;
  /** 更新时间（ISO） */
  updatedAt: string;
  /** 授权校区授权（列表接口随附；顾问指派等按校区过滤用） */
  campusGrants?: StaffCampusGrant[];
  /** 每周可排课时段（列表摘要源，5-3） */
  weeklyAvailability?: WeeklyAvailabilitySlot[];
  /** 临时不可排课日期（列表摘要源，5-3） */
  availabilityExceptions?: AvailabilityException[];
}

/** 员工详情（对齐后端 StaffResponseDto）。 */
export interface StaffDetail {
  /** 员工 ID（内部主键） */
  id: string;
  /** 员工编号（staff-{拼音}） */
  staffNo?: string | null;
  /** 姓名 */
  name: string;
  /** 手机号（后端返回脱敏值） */
  mobile?: string | null;
  /** 状态 */
  status: StaffStatus;
  /** 是否已绑定微信 */
  wechatBound: boolean;
  /** 微信昵称 */
  wechatNickname?: string | null;
  /** 微信头像 */
  wechatAvatar?: string | null;
  /** 微信绑定时间（ISO） */
  wechatBoundAt?: string | null;
  /** 微信绑定状态 */
  qrBindStatus: WechatBindStatus;
  /** 岗位列表 */
  positions: StaffPosition[];
  /** 授权校区列表 */
  campusGrants?: StaffCampusGrant[];
  /** 每周可排课时段（5-3 staff-availability；空数据为空数组） */
  weeklyAvailability?: WeeklyAvailabilitySlot[];
  /** 临时不可排课日期（5-3；空数据为空数组） */
  availabilityExceptions?: AvailabilityException[];
  /** 更新时间（ISO） */
  updatedAt: string;
}

/** 员工列表查询参数。 */
export interface StaffListQuery {
  /** 姓名模糊筛选 */
  name?: string;
  /** 岗位筛选 */
  position?: StaffPosition;
  /** 微信绑定状态筛选 */
  wechatStatus?: WechatBindStatus;
  /** 显式校区筛选（O-28：与上下文数据范围取交集，授权范围外 403） */
  campusId?: string;
  /** 页码 */
  page?: number;
  /** 每页条数 */
  pageSize?: number;
}

/** 新建员工请求体。 */
export interface CreateStaff {
  /** 姓名 */
  name: string;
  /** 联系手机号（必填） */
  mobile: string;
  /** 岗位列表（至少一个） */
  positions: StaffPosition[];
  /** 授权校区 ID（必填，新建时创建初始授权） */
  campusId: string;
}

/** 编辑员工基本信息请求体（partial）。 */
export interface UpdateStaff {
  /** 姓名 */
  name?: string;
  /** 联系手机号 */
  mobile?: string;
  /** 岗位列表 */
  positions?: StaffPosition[];
  /** 状态 */
  status?: StaffStatus;
}

/** 保存校区授权请求体（对齐后端 SaveStaffGrantsDto）。 */
export interface SaveStaffGrants {
  /** 授权列表 */
  grants: StaffCampusGrant[];
}

/** 微信绑定二维码响应。 */
export interface WechatBindQrcode {
  /** 绑定令牌（state，回调与轮询凭据） */
  token: string;
  /** 扫码授权 URL（embed=qrconnect，qrcode=服务号网页授权 URL，fake=mock:// 占位） */
  qrcodeUrl: string;
  /** 展示通道（后端 IKBFM0 起新增；旧实例缺失时前端按 qrcodeUrl 前缀推断） */
  mode?: WechatQrcodeMode;
  /** 过期时间（ISO） */
  expiresAt: string;
}

/** 微信绑定会话状态（轮询返回）。 */
export type WechatBindSessionStatus =
  "pending" | "confirmed" | "expired" | "failed";

/** 微信绑定状态轮询响应。 */
export interface WechatBindSession {
  /** 绑定状态 */
  status: WechatBindSessionStatus;
  /** 脱敏 openid（仅 confirmed 返回） */
  wechatOpenIdMasked?: string;
}

async function listStaffs(
  query: StaffListQuery = {},
): Promise<Paginated<StaffListItem>> {
  return http.get<Paginated<StaffListItem>>(ApiPaths.staffs.list, {
    ...(query.name ? { name: query.name } : {}),
    ...(query.position ? { position: query.position } : {}),
    ...(query.wechatStatus ? { wechatStatus: query.wechatStatus } : {}),
    ...(query.campusId ? { campusId: query.campusId } : {}),
    ...(query.page ? { page: query.page } : {}),
    ...(query.pageSize ? { pageSize: query.pageSize } : {}),
  });
}

async function createStaff(dto: CreateStaff): Promise<StaffDetail> {
  return http.post<StaffDetail>(ApiPaths.staffs.create, dto);
}

async function getStaff(id: string): Promise<StaffDetail> {
  return http.get<StaffDetail>(ApiPaths.staffs.detail(id));
}

async function updateStaff(id: string, dto: UpdateStaff): Promise<StaffDetail> {
  return http.patch<StaffDetail>(ApiPaths.staffs.update(id), dto);
}

async function suspendStaff(id: string): Promise<StaffDetail> {
  return http.post<StaffDetail>(ApiPaths.staffs.suspend(id), {});
}

async function activateStaff(id: string): Promise<StaffDetail> {
  return http.post<StaffDetail>(ApiPaths.staffs.activate(id), {});
}

async function saveStaffGrants(
  id: string,
  dto: SaveStaffGrants,
): Promise<void> {
  await http.put<void>(ApiPaths.staffs.grants(id), dto);
}

/** 全量替换员工每周可排课时段载荷（staff-availability:SC-06，items 为终态全量，id 不提交）。 */
export interface SaveStaffAvailability {
  items: Array<Pick<WeeklyAvailabilitySlot, 'dayOfWeek' | 'startTime' | 'endTime'>>;
}

/** 全量替换员工临时不可排课日期载荷（staff-availability:SC-09）。 */
export interface SaveStaffAvailabilityExceptions {
  items: Array<
    Pick<AvailabilityException, 'date' | 'allDay' | 'reason'> & {
      startTime?: string | null;
      endTime?: string | null;
    }
  >;
}

/** 全量替换员工每周可排课时段（编辑对话框「排课时段」页签保存）。 */
async function saveStaffAvailability(
  id: string,
  dto: SaveStaffAvailability,
): Promise<void> {
  await http.put<void>(ApiPaths.staffs.availability(id), dto);
}

/** 全量替换员工临时不可排课日期（编辑对话框「临时不可排课日期」页签保存）。 */
async function saveStaffAvailabilityExceptions(
  id: string,
  dto: SaveStaffAvailabilityExceptions,
): Promise<void> {
  await http.put<void>(ApiPaths.staffs.availabilityExceptions(id), dto);
}

async function generateBindQrcode(id: string): Promise<WechatBindQrcode> {
  return http.post<WechatBindQrcode>(ApiPaths.staffs.wechatBindQrcode(id), {});
}

/** 轮询微信绑定状态（长轮询，前端 2s 间隔）。 */
async function pollWechatBindSession(
  id: string,
  token: string,
): Promise<WechatBindSession> {
  return http.get<WechatBindSession>(
    ApiPaths.staffs.wechatBindSession(id, token),
  );
}

/** 触发微信回调（fake 模式模拟扫码：用固定 mock-scan-code 调回调端点）。 */
async function triggerWechatBindCallback(token: string): Promise<void> {
  await http.get<void>(ApiPaths.auth.wechatBindCallback, {
    code: "mock-scan-code",
    state: token,
  });
}

async function unbindWechat(id: string): Promise<void> {
  await http.delete<void>(ApiPaths.staffs.wechatUnbind(id));
}

/** 员工管理 API 唯一对外入口。 */
export const staffApi = {
  listStaffs,
  createStaff,
  getStaff,
  updateStaff,
  suspendStaff,
  activateStaff,
  saveStaffGrants,
  saveStaffAvailability,
  saveStaffAvailabilityExceptions,
  generateBindQrcode,
  pollWechatBindSession,
  triggerWechatBindCallback,
  unbindWechat,
};
